import OpenCallCard from "@/components/ui/open-call-card";
import { useAuth } from "@/contexts/auth-context";
import { fetchSacChamados } from "@/services/chamados";
import { SacChamado } from "@/types/Chamados";
import { Skeleton } from "@/components/ui/skeleton";
import { History } from "lucide-react";
import { useEffect, useState } from "react";

interface ChamadosHistoricoListProps {
  onSelectChamado: (chamado: SacChamado) => void;
}

export default function ChamadosHistoricoList({
  onSelectChamado,
}: ChamadosHistoricoListProps) {
  const { user } = useAuth();
  const [chamados, setChamados] = useState<SacChamado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadChamados = async () => {
      try {
        setLoading(true);

        const data = await fetchSacChamados(user.cli_codigo);

        const chamadosFechados = data.filter(
          (chamado) => chamado.sst_codigo === "3" || chamado.sst_codigo === "4",
        );
        setChamados(chamadosFechados);
      } catch (err) {
        setError("Erro ao carregar chamados");
        console.error("Erro ao carregar chamados:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.cli_codigo) {
      loadChamados();
    }
  }, [user?.cli_codigo]);

  if (loading) {
    return (
      <div className="w-full space-y-4">
        <div className="mb-4 sm:mb-6">
          <Skeleton className="h-7 w-56 mb-2" />
          <Skeleton className="h-4 w-36" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-[300px] text-center">
        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <History className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Nenhum histórico encontrado
        </h3>
        <p className="text-gray-500 max-w-sm">
          Não foram encontrados chamados finalizados.
        </p>
      </div>
    );
  }

  if (chamados.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 min-h-[300px] text-center">
        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <History className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Nenhum histórico encontrado
        </h3>
        <p className="text-gray-500 max-w-sm">
          Você ainda não possui chamados finalizados. O histórico aparecerá aqui
          quando seus chamados forem concluídos.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-n-green">
          Histórico de Chamados
        </h2>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">
          {chamados.length}{" "}
          {chamados.length === 1
            ? "chamado finalizado"
            : "chamados finalizados"}
        </p>
      </div>

      <div className="space-y-3">
        {chamados.map((chamado) => (
          <OpenCallCard
            key={chamado.sac_codigo}
            chamado={chamado}
            onViewDetails={() => onSelectChamado(chamado)}
          />
        ))}
      </div>
    </div>
  );
}
